import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';

import PageHeader from '../components/PageHeader.jsx';
import { listCustomers } from '../api/customers';

/**
 * Customers list — paginated, searchable.
 *
 * Each row is a tappable card showing business name + an optional
 * second line (contact name / city). Tapping navigates to
 * /customers/:id for the detail view (contact info + financial
 * summary). Floating "+ New" FAB at the bottom-right routes to
 * /customers/new for the add flow.
 *
 * Page-size 20; "Load more" extends the list rather than paginating
 * with page numbers (mobile-natural, mirrors Sales / Quotes pattern).
 */
export default function Customers() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // Pick up flash from incoming navigations — e.g. NewCustomer's
  // post-create redirect when no returnTo was set.
  const location = useLocation();
  const flash = location.state?.flash;

  // Reset to page 1 whenever search changes. Each effect run loads
  // page 1 fresh; subsequent "Load more" taps fetch later pages.
  useEffect(() => {
    setPage(1);
    setItems([]);
    setHasMore(false);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listCustomers({ page, pageSize: 20, search: search || undefined })
      .then((data) => {
        if (cancelled) return;
        setItems((curr) => (page === 1 ? data.items : [...curr, ...data.items]));
        setHasMore(page < (data.pages ?? 1));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load customers');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, search]);

  return (
    <main className="min-h-full px-4 pb-24">
      <PageHeader title="Customers" subtitle="Tap to view or add" icon={Users} />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {/* Search — debounced via React's natural batching since
          listCustomers is fast and the result set is typically small.
          If we ever need a real debounce, drop in lodash.debounce. */}
      <input
        type="search"
        placeholder="Search customers…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full h-10 px-3 mb-3 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
      />

      <section className="space-y-2">
        {loading && items.length === 0 && (
          <p className="text-small text-gray-500 text-center py-8">Loading…</p>
        )}
        {error && (
          <p role="alert" className="text-small text-danger text-center py-4">
            {error}
          </p>
        )}
        {!loading && items.length === 0 && !error && (
          <div className="text-center py-12">
            <Users className="h-10 w-10 mx-auto mb-3 text-gray-400" />
            <p className="text-body text-gray-700">
              {search ? 'No matches' : 'No customers yet'}
            </p>
            {!search && (
              <p className="text-small text-gray-500 mt-1">
                Tap + at the bottom to add your first.
              </p>
            )}
          </div>
        )}
        {items.map((c) => (
          <Link
            key={c.id}
            to={`/customers/${c.id}`}
            className="mb-card-tap flex items-center gap-3 no-underline"
          >
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900 truncate">
                {c.name}
              </p>
              <p className="text-caption text-gray-500 truncate">
                {[c.contact_name, c.city, c.phone]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </p>
            </div>
          </Link>
        ))}
        {hasMore && !loading && (
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            className="w-full min-h-touch text-small text-amber-700 font-medium py-3"
          >
            Load more
          </button>
        )}
      </section>

      {/* Floating "+ New" — same vocabulary as the Sales / Quotes
          create FABs. Sits above the safe-area inset on iOS so the
          home indicator doesn't overlap. */}
      <Link
        to="/customers/new"
        aria-label="New customer"
        className="fixed right-4 z-40 w-14 h-14 rounded-pill bg-amber-500 active:bg-amber-600 text-white shadow-lg flex items-center justify-center"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
      >
        <Plus className="h-6 w-6" />
      </Link>
    </main>
  );
}
